import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Plus, Trash2, Save, RotateCcw, Loader2, RefreshCw, Share2, MessageSquareQuote, ArrowUp, ArrowDown, Star, Eye, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { adminGet, adminPut, adminPost, adminDelete, reportError } from './adminApi';
import { QcBody, StarRating, fmtAvg, fmtDateTime } from '../easer/qualite/qcShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const monthValue = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const EMAIL = {
  sent: { label: 'E-mail envoyé', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30', icon: Mail },
  failed: { label: 'E-mail non parti', cls: 'bg-destructive/10 text-destructive border-destructive/30', icon: AlertTriangle },
  skipped: { label: 'Sans e-mail', cls: 'bg-secondary text-muted-foreground border-border', icon: Mail },
};

/** Back-office « Contrôle qualité » : grille des critères (éditable) + contrôles réalisés par les managers. */
export function AdminQualiteTab({ adminKey }) {
  const [view, setView] = useState('controles');
  const [criteria, setCriteria] = useState(null);
  const [customized, setCustomized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(monthValue());
  const [shared, setShared] = useState('all');
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const loadCriteria = useCallback(async () => {
    try {
      const r = await adminGet('/api/admin/qualite/criteres', adminKey);
      setCriteria(r.data.items || []); setCustomized(!!r.data.customized); setDirty(false);
    } catch (err) { reportError('critères qualité', err, (m) => toast.error(m || 'Chargement impossible')); setCriteria([]); }
  }, [adminKey]);

  const loadControls = useCallback(async () => {
    setData(null);
    try {
      const params = { limit: 300 };
      if (month) params.month = month;
      if (shared !== 'all') params.shared = shared === 'shared' ? 'true' : 'false';
      const r = await adminGet('/api/admin/qualite', adminKey, params);
      setData(r.data);
    } catch (err) { reportError('contrôles qualité', err, (m) => toast.error(m || 'Chargement impossible')); setData({ items: [], stats: {}, by_easer: [] }); }
  }, [adminKey, month, shared]);

  useEffect(() => { loadCriteria(); }, [loadCriteria]);
  useEffect(() => { loadControls(); }, [loadControls]);

  const edit = (updater) => { setCriteria((c) => updater(structuredClone(c))); setDirty(true); };
  const move = (i, dir) => edit((c) => { const j = i + dir; if (j < 0 || j >= c.length) return c; [c[i], c[j]] = [c[j], c[i]]; return c; });

  const saveCriteria = async () => {
    const cleaned = (criteria || []).filter((c) => c.label.trim());
    if (!cleaned.length) return toast.error('Ajoutez au moins un critère');
    if (!cleaned.some((c) => c.active)) return toast.error('Au moins un critère doit être actif');
    setSaving(true);
    try {
      const r = await adminPut('/api/admin/qualite/criteres', adminKey, {}, { criteria: cleaned });
      setCriteria(r.data.items); setCustomized(true); setDirty(false);
      toast.success('Grille des critères enregistrée');
    } catch (err) { reportError('enregistrement critères', err, (m) => toast.error(m || 'Enregistrement impossible')); }
    finally { setSaving(false); }
  };

  const resetCriteria = async () => {
    setSaving(true);
    try {
      const r = await adminPost('/api/admin/qualite/criteres/reset', adminKey);
      setCriteria(r.data.items); setCustomized(false); setDirty(false);
      toast.success('Grille par défaut restaurée');
    } catch (err) { reportError('reset critères', err, (m) => toast.error(m || 'Réinitialisation impossible')); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    const id = toDelete?.id;
    if (!id) return;
    try {
      await adminDelete(`/api/admin/qualite/${id}`, adminKey);
      toast.success('Contrôle supprimé');
      setToDelete(null); setDetail(null); loadControls();
    } catch (err) { reportError('suppression contrôle', err, (m) => toast.error(m || 'Suppression impossible')); }
  };

  const photoUrl = (id, index) => `${BACKEND_URL}/api/admin/qualite/${id}/photos/${index}?key=${encodeURIComponent(adminKey)}`;
  // la liste est allégée (sans vignettes) : le détail complet est rechargé à l'ouverture
  const openDetail = async (c) => {
    setDetail(c);
    try { const r = await adminGet(`/api/admin/qualite/${c.id}`, adminKey); setDetail(r.data.control); } catch (err) { reportError('détail contrôle', err); }
  };
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-qualite-tab">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><ClipboardCheck className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <h2 className="font-['Space_Grotesk'] text-xl font-semibold">Contrôle qualité des interventions</h2>
            <p className="text-sm text-muted-foreground">Les managers (tag Dolibarr « MANAGER ») évaluent une intervention en cours ou terminée depuis leur espace Easer. Chaque contrôle compte <strong>double</strong> par rapport à un avis client dans la note des Easers concernés, qui peuvent y répondre. Le client ne le voit que si le manager le met à sa disposition.</p>
          </div>
        </div>
        <Tabs value={view} onValueChange={setView} className="mt-4">
          <TabsList className="grid grid-cols-2 w-full max-w-md h-11">
            <TabsTrigger value="controles" data-testid="admin-qualite-tab-controles">Contrôles réalisés</TabsTrigger>
            <TabsTrigger value="criteres" data-testid="admin-qualite-tab-criteres">Grille des critères</TabsTrigger>
          </TabsList>
        </Tabs>
      </Card>

      {view === 'criteres' && (
        <Card className="p-5 space-y-4" data-testid="admin-qualite-criteria">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-semibold">Critères notés de 1 à 5 étoiles</h3>
              <p className="text-sm text-muted-foreground">Les critères désactivés ne sont plus proposés aux managers ; les contrôles déjà réalisés gardent leurs notes.{customized ? '' : ' Grille par défaut.'}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetCriteria} disabled={saving || !customized} data-testid="admin-qualite-criteria-reset"><RotateCcw className="h-4 w-4 mr-1" /> Par défaut</Button>
              <Button onClick={saveCriteria} disabled={saving || !dirty} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="admin-qualite-criteria-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Enregistrer</Button>
            </div>
          </div>
          {criteria === null ? <Skeleton className="h-40 rounded-xl" /> : (
            <ul className="space-y-2">
              {criteria.map((c, i) => (
                <li key={c.id || i} className={`rounded-xl border border-border p-3 grid gap-2 sm:grid-cols-[auto,1fr,1fr,auto] sm:items-center ${c.active ? '' : 'opacity-60'}`} data-testid={`admin-qualite-criterion-${c.id || i}`}>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Monter"><ArrowUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => move(i, 1)} disabled={i === criteria.length - 1} aria-label="Descendre"><ArrowDown className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Libellé</Label>
                    <Input value={c.label} onChange={(e) => edit((cs) => { cs[i].label = e.target.value; return cs; })} maxLength={60} data-testid={`admin-qualite-criterion-label-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Aide affichée au manager (facultatif)</Label>
                    <Input value={c.description || ''} onChange={(e) => edit((cs) => { cs[i].description = e.target.value; return cs; })} maxLength={160} placeholder="Ex. : arrivée à l'heure prévue, client prévenu en cas de retard" />
                  </div>
                  <div className="flex items-center gap-3 justify-end">
                    <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.active} onCheckedChange={(v) => edit((cs) => { cs[i].active = v; return cs; })} data-testid={`admin-qualite-criterion-active-${i}`} /> Actif</label>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => edit((cs) => cs.filter((_, idx) => idx !== i))} aria-label="Supprimer le critère" data-testid={`admin-qualite-criterion-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Button variant="outline" onClick={() => edit((cs) => [...cs, { id: '', label: '', description: '', active: true }])} disabled={(criteria || []).length >= 12} data-testid="admin-qualite-criterion-add"><Plus className="h-4 w-4 mr-1" /> Ajouter un critère</Button>
        </Card>
      )}

      {view === 'controles' && (
        <>
          <Card className="p-4 flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="qc-month" className="text-xs text-muted-foreground">Mois</Label>
              <Input id="qc-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" data-testid="admin-qualite-month" />
            </div>
            <Tabs value={shared} onValueChange={setShared}>
              <TabsList className="h-10">
                <TabsTrigger value="all" data-testid="admin-qualite-filter-all">Tous</TabsTrigger>
                <TabsTrigger value="shared" data-testid="admin-qualite-filter-shared">Partagés client</TabsTrigger>
                <TabsTrigger value="internal" data-testid="admin-qualite-filter-internal">Internes</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="outline" className="h-10 ml-auto" onClick={loadControls} data-testid="admin-qualite-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
          </Card>

          {data && (
            <div className="grid sm:grid-cols-4 gap-3" data-testid="admin-qualite-stats">
              <Card className="p-4"><p className="text-xs text-muted-foreground">Contrôles</p><p className="text-2xl font-semibold" data-testid="admin-qualite-stat-count">{data.stats?.count ?? 0}</p></Card>
              <Card className="p-4"><p className="text-xs text-muted-foreground">Moyenne</p><p className="text-2xl font-semibold">{fmtAvg(data.stats?.average)} <span className="text-sm text-muted-foreground font-normal">/ 5</span></p></Card>
              <Card className="p-4"><p className="text-xs text-muted-foreground">Partagés avec le client</p><p className="text-2xl font-semibold">{data.stats?.shared ?? 0}</p></Card>
              <Card className="p-4"><p className="text-xs text-muted-foreground">Avec réponse Easer</p><p className="text-2xl font-semibold">{data.stats?.replied ?? 0}</p></Card>
            </div>
          )}

          {data?.by_easer?.length > 0 && (
            <Card className="p-4" data-testid="admin-qualite-by-easer">
              <h3 className="font-semibold mb-2">Par Easer (période)</h3>
              <div className="flex flex-wrap gap-2">
                {data.by_easer.map((e) => (
                  <span key={e.easer_id} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-sm"><span className="font-medium">{e.easer_name}</span><StarRating value={e.average} size="h-3.5 w-3.5" /><span className="text-muted-foreground">{fmtAvg(e.average)} · {e.count}</span></span>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-0 overflow-hidden">
            {!data && <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>}
            {data && items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground" data-testid="admin-qualite-empty">Aucun contrôle qualité sur cette période.</p>}
            {data && items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-qualite-table">
                  <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="py-2 px-3">Date</th><th className="py-2 px-3">Intervention</th><th className="py-2 px-3">Easer(s)</th><th className="py-2 px-3">Manager</th><th className="py-2 px-3">Note</th><th className="py-2 px-3">Client</th><th className="py-2 px-3"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((c) => {
                      const em = EMAIL[c.email_status];
                      return (
                        <tr key={c.id} className="hover:bg-secondary/40" data-testid={`admin-qualite-row-${c.id}`}>
                          <td className="py-2 px-3 whitespace-nowrap text-muted-foreground">{fmtDateTime(c.created_at)}</td>
                          <td className="py-2 px-3"><p className="font-medium">{c.label}</p><p className="text-xs text-muted-foreground">{c.client_name}{c.fichinter_ref ? ` · ${c.fichinter_ref}` : ''}</p></td>
                          <td className="py-2 px-3">{(c.easer_names || []).join(', ')}</td>
                          <td className="py-2 px-3 text-muted-foreground">{c.manager_name}</td>
                          <td className="py-2 px-3 whitespace-nowrap"><span className="inline-flex items-center gap-1 font-semibold"><Star className="h-3.5 w-3.5 fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]" /> {fmtAvg(c.average)}</span>{c.reply && <Badge className="ml-2 border text-[10px] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30"><MessageSquareQuote className="h-3 w-3 mr-1" /> Réponse</Badge>}</td>
                          <td className="py-2 px-3">
                            {c.share_with_client ? (
                              <span className="inline-flex flex-wrap gap-1"><Badge className="border text-[10px] bg-[hsl(var(--brand-sky))]/40 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sky))]"><Share2 className="h-3 w-3 mr-1" /> Partagé</Badge>{em && <Badge className={`border text-[10px] ${em.cls}`} title={c.email_error || ''}><em.icon className="h-3 w-3 mr-1" /> {em.label}</Badge>}</span>
                            ) : <span className="text-xs text-muted-foreground">Interne</span>}
                          </td>
                          <td className="py-2 px-3 text-right whitespace-nowrap">
                            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => openDetail(c)} aria-label="Voir" data-testid={`admin-qualite-view-${c.id}`}><Eye className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 px-2 text-destructive" onClick={() => setToDelete(c)} aria-label="Supprimer" data-testid={`admin-qualite-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-qualite-detail">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-[hsl(var(--primary))]" /> {detail?.label}</DialogTitle>
            <DialogDescription>{detail?.client_name} · {(detail?.easer_names || []).join(', ')}{detail?.fichinter_ref ? ` · ${detail.fichinter_ref}` : ''}</DialogDescription>
          </DialogHeader>
          {detail && <QcBody control={detail} photoUrl={photoUrl} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="admin-qualite-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contrôle qualité ?</AlertDialogTitle>
            <AlertDialogDescription>Il disparaîtra de l'espace des Easers concernés et de l'Espace Client, et ne comptera plus dans leur note. Cette action est définitive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-qualite-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="admin-qualite-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
